import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export interface CartItem {
  pharmacyProductId: string;
  name: string;
  chainName: string;
  price: string;
  quantity: number;
  addedAt: string;
}

interface MedicationCartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'addedAt'>) => boolean;
  removeItem: (pharmacyProductId: string) => void;
  updateQuantity: (pharmacyProductId: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
}

const STORAGE_KEY = 'medication-cart';

const MedicationCartContext = createContext<MedicationCartContextType | undefined>(undefined);

export const useMedicationCart = () => {
  const context = useContext(MedicationCartContext);
  if (!context) {
    throw new Error('useMedicationCart must be used within a MedicationCartProvider');
  }
  return context;
};

function loadCart(): CartItem[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed: any[] = JSON.parse(stored);
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    const isLegacy = parsed.some(
      (i) =>
        typeof i !== 'object' ||
        i === null ||
        !('price' in i) ||
        !('quantity' in i) ||
        'presentationId' in i,
    );
    if (isLegacy) return [];
    return parsed as CartItem[];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export const MedicationCartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  useEffect(() => {
    const handler = () => setItems([]);
    window.addEventListener('cart:clear', handler);
    return () => window.removeEventListener('cart:clear', handler);
  }, []);

  const addItem = useCallback((item: Omit<CartItem, 'addedAt'>): boolean => {
    const exists = items.some((i) => i.pharmacyProductId === item.pharmacyProductId);
    if (exists) return false;
    setItems((prev) => [...prev, { ...item, addedAt: new Date().toISOString() }]);
    return true;
  }, [items]);

  const removeItem = useCallback((pharmacyProductId: string) => {
    setItems((prev) => prev.filter((i) => i.pharmacyProductId !== pharmacyProductId));
  }, []);

  const updateQuantity = useCallback((pharmacyProductId: string, quantity: number) => {
    const next = Math.max(1, Math.floor(quantity));
    setItems((prev) =>
      prev.map((i) => (i.pharmacyProductId === pharmacyProductId ? { ...i, quantity: next } : i)),
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  const value: MedicationCartContextType = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    itemCount,
  };

  return (
    <MedicationCartContext.Provider value={value}>
      {children}
    </MedicationCartContext.Provider>
  );
};
